/**
 * Campos file/files guardam path interno (/uploads/uuid...); nome amigável vem de text_value (file único)
 * ou de entradas { path, name } em file_paths (múltiplos).
 */

import { normalizeStoragePathForDownload } from './fileDownload';

export type ParsedCustomFieldFileEntry = {
  path: string;
  displayName: string;
  /** Índice na lista normalizada (para reparo admin em campos `files`). */
  fileIndex: number;
  fileMissing?: boolean;
};

export function getSingleCustomFieldFileDisplayName(
  filePath: string | null | undefined,
  textValue: string | null | undefined
): string {
  const fromText = (textValue || '').trim();
  if (fromText) return fromText;
  if (!filePath) return 'Arquivo';
  const norm = normalizeStoragePathForDownload(filePath);
  return norm.split('/').pop() || 'Arquivo';
}

function extractPathFromObject(entry: object): string | undefined {
  const o = entry as Record<string, unknown>;
  const p = o.path ?? o.file_path;
  if (typeof p === 'string' && p.trim()) return p.trim();
  return undefined;
}

export function parseCustomFieldFilePathsJson(
  raw: string | null | undefined
): ParsedCustomFieldFileEntry[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(String(raw).trim());
    let items: unknown[] = [];
    if (Array.isArray(parsed)) {
      items = parsed;
    } else if (typeof parsed === 'string' && parsed.trim()) {
      // Legado: um único path serializado como string JSON
      items = [parsed.trim()];
    } else if (parsed && typeof parsed === 'object') {
      // Legado: um único objeto no lugar do array
      items = [parsed];
    } else {
      return [];
    }

    const rows = items
      .map((entry: unknown, idx: number) => {
        if (typeof entry === 'string' && entry.trim()) {
          const path = normalizeStoragePathForDownload(entry.trim());
          return {
            path,
            displayName: path.split('/').pop() || `Arquivo ${idx + 1}`,
          };
        }
        if (entry && typeof entry === 'object') {
          const rawPath = extractPathFromObject(entry as object);
          if (!rawPath) return null;
          const path = normalizeStoragePathForDownload(rawPath);
          const o = entry as Record<string, unknown>;
          const nameRaw = o.name ?? o.filename;
          const displayName =
            (typeof nameRaw === 'string' && nameRaw.trim()) ||
            path.split('/').pop() ||
            `Arquivo ${idx + 1}`;
          const fileMissing = o.file_missing === true || o.file_missing === 'true';
          return { path, displayName, fileMissing };
        }
        return null;
      })
      .filter((r): r is { path: string; displayName: string; fileMissing?: boolean } => r != null);
    return rows.map((r, i) => ({ ...r, fileIndex: i }));
  } catch {
    // Legado: valor não é JSON (ex.: só o path ou só o nome do ficheiro)
    const t = String(raw).trim();
    if (!t) return [];
    const path = normalizeStoragePathForDownload(t);
    return [{ path, displayName: path.split('/').pop() || 'Arquivo', fileIndex: 0 }];
  }
}
